import type { WorkspaceAuditAction } from './types'

type AuditActionInfo = { group: string; label: string }

// Shared wording keeps the action pills and filter in sync with API actions.
//
// Deliberately a Partial record rather than an exhaustive one: the backend
// can ship new WorkspaceAuditAction enum values independently of this file.
// Until someone adds a label here, the pill falls back to "Unknown event"
// (see AuditActionPill) and the action is simply absent from the filter
// (see actionFilterOptions), rather than breaking the build for every
// consumer of this schema. Add new actions in visibleAuditActions.ts too,
// so they stop being treated as hidden/unknown.
export const auditActions: Partial<
  Record<WorkspaceAuditAction, AuditActionInfo>
> = {
  CONVERTED_TO_INVOICE_PAYMENT: {
    group: 'Billing',
    label: 'Switched to invoice billing',
  },
  CANCEL_LICENSE: { group: 'Billing', label: 'Editor seat cancelled' },
  GENERATE_DOCUMENTS_FEED: {
    group: 'Documents',
    label: 'Document feed generated',
  },
  APPROVED_EDUCATION_REQUEST: {
    group: 'Education',
    label: 'Education application approved',
  },
  REJECT_EDUCATION_REQUEST: {
    group: 'Education',
    label: 'Education application rejected',
  },
  NEW_EDUCATION_REQUEST: {
    group: 'Education',
    label: 'Education application submitted',
  },
  REQUEST_INFO_EDUCATION_REQUEST: {
    group: 'Education',
    label: 'Education information requested',
  },
  CONVERT_TO_EDUCATION_WORKSPACE: {
    group: 'Education',
    label: 'Converted to education',
  },
  ATTACH_FEATURE_FLAG: { group: 'Feature Flags', label: 'Feature enabled' },
  DETACH_FEATURE_FLAG: { group: 'Feature Flags', label: 'Feature disabled' },
  CREATE_PERMISSION_GROUP: { group: 'Groups', label: 'Group created' },
  CREATE_PERMISSION_GROUP_RELATION: {
    group: 'Groups',
    label: 'Group access added',
  },
  ADD_MEMBER_PERMISSION_GROUP: {
    group: 'Groups',
    label: 'Group members added',
  },
  UPDATE_PERMISSION_GROUP: { group: 'Groups', label: 'Group renamed' },
  DELETE_PERMISSION_GROUP: { group: 'Groups', label: 'Group deleted' },
  REMOVE_MEMBER_PERMISSION_GROUP: {
    group: 'Groups',
    label: 'Group member removed',
  },
  REMOVE_PERMISSION_GROUP_RELATION: {
    group: 'Groups',
    label: 'Group access removed',
  },
  INSTALL_INTEGRATION: {
    group: 'Integrations',
    label: 'Integration installed',
  },
  UNINSTALL_INTEGRATION: {
    group: 'Integrations',
    label: 'Integration uninstalled',
  },
  CREATE_MEMBERSHIP: { group: 'Membership', label: 'Member invited' },
  UPDATE_MEMBERSHIP: { group: 'Membership', label: 'Member updated' },
  DELETE_MEMBERSHIP: { group: 'Membership', label: 'Membership removed' },
  ACCEPT_MEMBERSHIP_INVITE: {
    group: 'Membership',
    label: 'Invitation accepted',
  },
  DISABLE_MEMBERSHIP: { group: 'Membership', label: 'Member disabled' },
  RESTORE_MEMBERSHIP: { group: 'Membership', label: 'Member restored' },
  SSO_MEMBERSHIP_PROVISIONED: {
    group: 'Membership',
    label: 'SSO Editor added',
  },
  DOWNGRADED_SSO_MEMBERSHIP_PROVISIONED: {
    group: 'Membership',
    label: 'SSO Viewer added',
  },
  UNKNOWN: { group: 'Other', label: 'Unknown event' },
  CHANGED_PLAN: { group: 'Plan', label: 'Plan changed' },
  CREATE_PREVIEW: { group: 'Previews', label: 'Preview created' },
  UPDATE_PREVIEW: { group: 'Previews', label: 'Preview updated' },
  ADD_PREVIEW_PASSWORD: {
    group: 'Previews',
    label: 'Preview password added',
  },
  REMOVE_PREVIEW_PASSWORD: {
    group: 'Previews',
    label: 'Preview password removed',
  },
  REFRESH_PREVIEW: { group: 'Previews', label: 'Preview link refreshed' },
  DELETE_PREVIEW: { group: 'Previews', label: 'Preview deleted' },
  CHANGE_WORKSPACE_PROFILE_ENABLED: {
    group: 'Profile',
    label: 'Public profile updated',
  },
  CHANGE_WORKSPACE_PROFILE_IS_OFFICIAL: {
    group: 'Profile',
    label: 'Official status changed',
  },
  CHANGE_WORKSPACE_PROFILE_SHORT_URL: {
    group: 'Profile',
    label: 'Profile URL changed',
  },
  CHANGE_WORKSPACE_PROFILE_VISIBILITY: {
    group: 'Profile',
    label: 'Profile visibility changed',
  },
  ARCHIVE_PROJECT: { group: 'Projects', label: 'Project archived' },
  UNARCHIVE_PROJECT: { group: 'Projects', label: 'Project unarchived' },
  CHANGE_ACCOUNT_LINKING: {
    group: 'Security',
    label: 'Account linking changed',
  },
  CHANGE_SCIM_AUTO_LINK: { group: 'Security', label: 'SCIM linking changed' },
  CHANGE_SECURITY_EMAIL: { group: 'Security', label: 'Security email changed' },
  CONFIGURE_ENCRYPTION_KEYS: {
    group: 'Security',
    label: 'Encryption keys updated',
  },
  RESET_SCIM_TOKEN: { group: 'Security', label: 'SCIM token reset' },
  SET_SCIM_ENABLED: { group: 'Security', label: 'SCIM provisioning changed' },
  SET_SSO_CONFIG: { group: 'SSO', label: 'SSO configuration updated' },
  SET_SSO_ELIGIBILITY: { group: 'SSO', label: 'SSO availability changed' },
  DISABLE_SSO: { group: 'SSO', label: 'SSO disabled' },
  SUPPORT_ACCESS_ENABLED: { group: 'Support', label: 'Support access enabled' },
  SUPPORT_ACCESS_DISABLED: {
    group: 'Support',
    label: 'Support access disabled',
  },
  TRIAL_DURATION: { group: 'Trial', label: 'Trial duration changed' },
  CHANGE_UNLIMITED_TRIAL: { group: 'Trial', label: 'Unlimited trial changed' },
  CREATE_WORKSPACE: { group: 'Workspace', label: 'Workspace created' },
  UPDATE_WORKSPACE: { group: 'Workspace', label: 'Workspace updated' },
  CHANGE_RESTRICTED_UPLOADS: {
    group: 'Workspace',
    label: 'Upload restrictions changed',
  },
  SET_RESTRICTED_UPLOADS_ELIGIBILITY: {
    group: 'Workspace',
    label: 'Upload restriction availability changed',
  },
  SET_WORKSPACE_EXPIRED_VERSIONS_RULES: {
    group: 'Workspace',
    label: 'Version retention changed',
  },
  DELETE_ORGANIZATION: { group: 'Workspace', label: 'Workspace deleted' },
  TRANSFER_ORGANIZATION: { group: 'Workspace', label: 'Ownership transferred' },
  UPGRADE_PERSONAL_WORKSPACE: {
    group: 'Workspace',
    label: 'Personal workspace upgraded',
  },
  CHANGE_UNLIMITED_STORAGE_OVERRIDE_ENABLED: {
    group: 'Workspace',
    label: 'Unlimited storage changed',
  },
  SET_DESIGN_SYSTEMS_ELIGIBILITY: {
    group: 'Workspace',
    label: 'Design systems availability changed',
  },
}
